import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyProductKey,
  ShopifyProductToSellingPlanGroupKey,
  ShopifyProductVariantKey,
  ShopifyProductVariantToSellingPlanGroupKey,
  ShopifySellingPlanGroupKey,
  ShopifySellingPlanGroupProductKey,
  ShopifySellingPlanGroupProductToProductKey,
  ShopifySellingPlanGroupProductToSellingPlanGroupKey,
  ShopifySellingPlanGroupProductVariantKey,
  ShopifySellingPlanGroupProductVariantToProductVariantKey,
  ShopifySellingPlanGroupProductVariantToSellingPlanGroupKey,
  ShopifySellingPlanGroupToProductKey,
  ShopifySellingPlanGroupToProductVariantKey,
  ShopifySellingPlanGroupToSellingPlanGroupProductVariantsKey,
  ShopifySellingPlanGroupToSellingPlanGroupProductsKey,
  ShopifySellingPlanGroupToShopKey,
  ShopifySellingPlanKey,
  ShopifySellingPlanToSellingPlanGroupKey,
  ShopifyShopKey,
} from "./keys";
import { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

// we added shopify field keys in 2025-04 which changes how the static keys are generated. these are here so that they remain the same
const appIdKey = "ModelField-DataModel-Shopify-SellingPlanGroup-appId";
const appIdStorageEpochKey = "FieldStorageEpoch-DataModel-Shopify-SellingPlanGroup-appId-initial";
const merchantCodeKey = "ModelField-DataModel-Shopify-SellingPlanGroup-merchantCode";
const merchantCodeStorageEpochKey = "FieldStorageEpoch-DataModel-Shopify-SellingPlanGroup-merchantCode-initial";

const specs: ConnectionModelSpecVersion = {
  key: ShopifySellingPlanGroupKey,
  "2022-01": {
    name: "Shopify Selling Plan Group",
    graphQLTypename: "SellingPlanGroup",
    fields: [
      {
        name: "App ID",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "appId" } },
      },
      {
        name: "Shopify Created At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "createdAt" } },
      },
      {
        name: "Description",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "description" } },
      },
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "id" } },
      },
      {
        name: "Merchant Code",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "merchantCode" } },
      },
      {
        name: "Name",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "name" } },
      },
      {
        name: "Options",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "options" } },
      },
      {
        name: "Position",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "position" } },
        configuration: {
          decimals: 0,
        },
      },
      {
        name: "Summary",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "summary" } },
      },
      {
        name: "Product Variants",
        fieldType: FieldType.HasManyThrough,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "productVariants" } },
        key: ShopifySellingPlanGroupToProductVariantKey,
        configuration: {
          joinModelKey: ShopifySellingPlanGroupProductVariantKey,
          relatedModelKey: ShopifyProductVariantKey,
          inverseJoinModelFieldKey: ShopifySellingPlanGroupProductVariantToSellingPlanGroupKey,
          inverseRelatedModelFieldKey: ShopifySellingPlanGroupProductVariantToProductVariantKey,
          inverseFieldKey: ShopifyProductVariantToSellingPlanGroupKey,
        },
      },
      {
        name: "Selling Plan Group Product Variants",
        fieldType: FieldType.HasMany,
        key: ShopifySellingPlanGroupToSellingPlanGroupProductVariantsKey,
        configuration: {
          dependent: "delete",
          relatedModelKey: ShopifySellingPlanGroupProductVariantKey,
          inverseFieldKey: ShopifySellingPlanGroupProductVariantToSellingPlanGroupKey,
        },
      },
      {
        name: "Products",
        fieldType: FieldType.HasManyThrough,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "products" } },
        key: ShopifySellingPlanGroupToProductKey,
        configuration: {
          joinModelKey: ShopifySellingPlanGroupProductKey,
          relatedModelKey: ShopifyProductKey,
          inverseJoinModelFieldKey: ShopifySellingPlanGroupProductToSellingPlanGroupKey,
          inverseRelatedModelFieldKey: ShopifySellingPlanGroupProductToProductKey,
          inverseFieldKey: ShopifyProductToSellingPlanGroupKey,
        },
      },
      {
        name: "Selling Plan Group Products",
        fieldType: FieldType.HasMany,
        key: ShopifySellingPlanGroupToSellingPlanGroupProductsKey,
        configuration: {
          dependent: "delete",
          relatedModelKey: ShopifySellingPlanGroupProductKey,
          inverseFieldKey: ShopifySellingPlanGroupProductToSellingPlanGroupKey,
        },
      },
      {
        name: "Selling Plans",
        fieldType: FieldType.HasMany,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "sellingPlans" } },
        configuration: {
          dependent: "delete",
          relatedModelKey: ShopifySellingPlanKey,
          inverseFieldKey: ShopifySellingPlanToSellingPlanGroupKey,
        },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifySellingPlanGroupToShopKey,
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: {
      type: "graphql_list",
      resource: "sellingPlanGroups",
      filters: {},
      sortableBy: {
        createdAt: { sortKey: "CREATED_AT", reverse: true },
        updatedAt: { sortKey: "UPDATED_AT", reverse: true },
      },
    },
  },
  "2025-04": {
    fields: [
      {
        name: "App ID",
        key: appIdKey,
        initialStorageEpochKey: appIdStorageEpochKey,
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "appId",
            availableInWebhook: ["selling_plan_groups/create", "selling_plan_groups/update"],
            shopifyFieldKey: "app_id",
          },
        },
      },
      {
        name: "Description",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "description",
            availableInWebhook: ["selling_plan_groups/create", "selling_plan_groups/update"],
            shopifyFieldKey: "description",
          },
        },
      },
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "id",
            availableInWebhook: ["selling_plan_groups/create", "selling_plan_groups/update"],
            shopifyFieldKey: "id",
          },
        },
      },
      {
        name: "Merchant Code",
        key: merchantCodeKey,
        initialStorageEpochKey: merchantCodeStorageEpochKey,
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "merchantCode",
            availableInWebhook: ["selling_plan_groups/create", "selling_plan_groups/update"],
            shopifyFieldKey: "merchant_code",
          },
        },
      },
      {
        name: "Name",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "name",
            availableInWebhook: ["selling_plan_groups/create", "selling_plan_groups/update"],
            shopifyFieldKey: "name",
          },
        },
      },
      {
        name: "Options",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "options",
            availableInWebhook: ["selling_plan_groups/create", "selling_plan_groups/update"],
            shopifyFieldKey: "options",
          },
        },
      },
      {
        name: "Position",
        fieldType: FieldType.Number,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "position",
            availableInWebhook: ["selling_plan_groups/create", "selling_plan_groups/update"],
            shopifyFieldKey: "position",
          },
        },
        configuration: {
          decimals: 0,
        },
      },
      {
        name: "Summary",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "summary",
            availableInWebhook: ["selling_plan_groups/create", "selling_plan_groups/update"],
            shopifyFieldKey: "summary",
          },
        },
      },
      {
        name: "Product Variants",
        fieldType: FieldType.HasManyThrough,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "productVariants",
            availableInWebhook: ["selling_plan_groups/create", "selling_plan_groups/update"],
            shopifyFieldKey: "product_variants",
          },
        },
        key: ShopifySellingPlanGroupToProductVariantKey,
        configuration: {
          joinModelKey: ShopifySellingPlanGroupProductVariantKey,
          relatedModelKey: ShopifyProductVariantKey,
          inverseJoinModelFieldKey: ShopifySellingPlanGroupProductVariantToSellingPlanGroupKey,
          inverseRelatedModelFieldKey: ShopifySellingPlanGroupProductVariantToProductVariantKey,
          inverseFieldKey: ShopifyProductVariantToSellingPlanGroupKey,
        },
      },
      {
        name: "Products",
        fieldType: FieldType.HasManyThrough,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "products",
            availableInWebhook: ["selling_plan_groups/create", "selling_plan_groups/update"],
            shopifyFieldKey: "products",
          },
        },
        key: ShopifySellingPlanGroupToProductKey,
        configuration: {
          joinModelKey: ShopifySellingPlanGroupProductKey,
          relatedModelKey: ShopifyProductKey,
          inverseJoinModelFieldKey: ShopifySellingPlanGroupProductToSellingPlanGroupKey,
          inverseRelatedModelFieldKey: ShopifySellingPlanGroupProductToProductKey,
          inverseFieldKey: ShopifyProductToSellingPlanGroupKey,
        },
      },
      {
        name: "Selling Plans",
        fieldType: FieldType.HasMany,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "sellingPlans",
            availableInWebhook: ["selling_plan_groups/create", "selling_plan_groups/update"],
            shopifyFieldKey: "selling_plans",
          },
        },
        configuration: {
          dependent: "delete",
          relatedModelKey: ShopifySellingPlanKey,
          inverseFieldKey: ShopifySellingPlanToSellingPlanGroupKey,
        },
      },
    ],
  },
};

export const ShopifySellingPlanGroup = ({
  apiVersion,
  frameworkVersionConstraint,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint?: string;
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint);
